import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../data-table";
import {
  Mail,
  Loader2,
  Send,
  Users,
  FileText,
  Hash,
  Clock,
  AlertCircle,
  AtSign,
  Link2,
  Code,
  MessageSquare,
} from "lucide-react";
import type { EmailLog } from "../../services/api";
import {
  formatLogTimestamp,
  formatTypeLabel,
  getEmailBcc,
  getEmailCc,
  getEmailCreatedAt,
  getEmailCreatedBy,
  getEmailDeliveredAt,
  getEmailErrorMessage,
  getEmailExternalId,
  getEmailFrom,
  getEmailHtmlBody,
  getEmailPlainText,
  getEmailRecipientCount,
  getEmailRecipients,
  getEmailReplyTo,
  getEmailSentAt,
  getEmailStatus,
  getEmailSubject,
  getEmailTenantId,
  getEmailTo,
  getEmailType,
  getEmailUpdatedAt,
  getEmailUpdatedBy,
  type EmailRecipientRow,
} from "../../lib/communicationLogUtils";

interface EmailLogDetailsModalProps {
  open: boolean;
  log: EmailLog | null;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
}

const recipientTableHeadClass =
  "h-8 text-[11px] font-bold uppercase tracking-wide text-zinc-500";

export function EmailLogDetailsModal({
  open,
  log,
  loading,
  onOpenChange,
}: EmailLogDetailsModalProps) {
  const sentAt = log ? formatLogTimestamp(getEmailSentAt(log)) : "—";
  const deliveredAt = log?.delivered_at
    ? formatLogTimestamp(getEmailDeliveredAt(log))
    : "—";
  const statusLabel = log ? getEmailStatus(log) : "—";
  const recipients = log ? getEmailRecipients(log) : [];
  const errorMessage = log ? getEmailErrorMessage(log) : "—";
  const plainBody = log ? getEmailPlainText(log) : "—";
  const htmlBody = log ? getEmailHtmlBody(log) : "—";

  const recipientColumns = useMemo<TableColumnDef<EmailRecipientRow>[]>(
    () => [
      {
        id: "role",
        label: "Role",
        width: 100,
        headerClassName: recipientTableHeadClass,
        renderCell: row => (
          <span className="font-medium text-zinc-800">{row.role}</span>
        ),
      },
      {
        id: "email",
        label: "Email address",
        width: 320,
        headerClassName: recipientTableHeadClass,
        renderCell: row => (
          <span
            className="block truncate text-sm text-zinc-800"
            title={row.email}
          >
            {row.email}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
        <DialogHeader className="border-b border-zinc-200 bg-white px-6 pb-4 pr-14 pt-5 text-left">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight text-zinc-900">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]"
                aria-hidden
              >
                <Mail className="h-5 w-5 text-[#8B3232]" strokeWidth={2.25} />
              </span>
              Email Log Details
            </DialogTitle>
            {log && !loading && statusLabel !== "—" && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#f0dede] bg-[#FDF7F7] px-3.5 py-1 text-sm font-semibold capitalize text-[#8B3232]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-[#c53030]"
                  aria-hidden
                />
                {statusLabel}
              </span>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex h-[400px] flex-col items-center justify-center gap-4 bg-white">
            <Loader2 className="h-12 w-12 animate-spin text-[#8B3232]" />
            <p className="animate-pulse font-medium text-muted-foreground">
              Fetching email log details...
            </p>
          </div>
        ) : (
          log && (
            <>
              <div className="max-h-[min(80vh,730px)] overflow-y-auto bg-white">
                {/* Summary */}
                <div className="border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5">
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                    <div className="min-w-0 space-y-1 sm:flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                        Subject
                      </p>
                      <p className="text-lg font-semibold leading-snug tracking-tight text-zinc-900 sm:text-xl">
                        {getEmailSubject(log)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12 md:gap-x-16">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Sent at
                        </p>
                        <p className="text-sm font-semibold text-zinc-900">
                          {sentAt}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Email type
                        </p>
                        <p className="text-sm font-semibold capitalize text-zinc-900">
                          {formatTypeLabel(getEmailType(log))}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Recipients
                        </p>
                        <p className="text-sm font-semibold text-zinc-900">
                          {getEmailRecipientCount(log)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                  {/* Envelope */}
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <Send className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Envelope
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5">
                      <DetailItem
                        icon={<Mail className="h-3.5 w-3.5" />}
                        label="From"
                        value={getEmailFrom(log)}
                      />
                      <DetailItem
                        icon={<AtSign className="h-3.5 w-3.5" />}
                        label="To"
                        value={getEmailTo(log)}
                      />
                      <DetailItem
                        icon={<Users className="h-3.5 w-3.5" />}
                        label="CC"
                        value={getEmailCc(log)}
                      />
                      <DetailItem
                        icon={<Users className="h-3.5 w-3.5" />}
                        label="BCC"
                        value={getEmailBcc(log)}
                      />
                      <DetailItem
                        icon={<Link2 className="h-3.5 w-3.5" />}
                        label="Reply-To"
                        value={getEmailReplyTo(log)}
                      />
                    </div>
                  </div>

                  {/* Delivery */}
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <FileText className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Delivery
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5">
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Status"
                        value={getEmailStatus(log)}
                      />
                      <DetailItem
                        icon={<Hash className="h-3.5 w-3.5" />}
                        label="External ID"
                        value={getEmailExternalId(log)}
                      />
                      <DetailItem
                        icon={<Clock className="h-3.5 w-3.5" />}
                        label="Delivered at"
                        value={deliveredAt}
                      />
                      <DetailItem
                        icon={<Clock className="h-3.5 w-3.5" />}
                        label="Created on"
                        value={formatLogTimestamp(getEmailCreatedAt(log))}
                      />
                      <DetailItem
                        icon={<Clock className="h-3.5 w-3.5" />}
                        label="Updated on"
                        value={formatLogTimestamp(getEmailUpdatedAt(log))}
                      />
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <Hash className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Record metadata
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5 sm:grid-cols-2">
                      <DetailItem
                        icon={<Hash className="h-3.5 w-3.5" />}
                        label="Log ID"
                        value={String(log.id)}
                      />
                      <DetailItem
                        icon={<Hash className="h-3.5 w-3.5" />}
                        label="Tenant ID"
                        value={getEmailTenantId(log)}
                      />
                      <DetailItem
                        icon={<Users className="h-3.5 w-3.5" />}
                        label="Created by"
                        value={getEmailCreatedBy(log)}
                      />
                      <DetailItem
                        icon={<Users className="h-3.5 w-3.5" />}
                        label="Updated by"
                        value={getEmailUpdatedBy(log)}
                      />
                    </div>
                  </div>

                  {/* Error message */}
                  {errorMessage !== "—" && (
                    <div className="md:col-span-2">
                      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <AlertCircle className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Error details
                      </h3>
                      <div className="rounded-lg border border-red-200 bg-red-50/50 py-4 pl-4 pr-4">
                        <p className="border-l-[3px] border-red-400 pl-4 font-mono text-xs leading-relaxed text-red-800">
                          {errorMessage}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Recipients table */}
                  <DataTableProvider
                    tableId="super-admin-email-log-recipients"
                    columns={recipientColumns}
                  >
                    <div className="space-y-3 md:col-span-2">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="flex items-center gap-2.5 text-base font-bold text-zinc-900">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                            <Users className="h-4 w-4 text-[#8B3232]" />
                          </span>
                          Recipients
                          {recipients.length > 0 && (
                            <span className="ml-1 text-sm font-normal text-muted-foreground">
                              ({recipients.length})
                            </span>
                          )}
                        </h3>
                        <DataTableColumnSettings columns={recipientColumns} />
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-zinc-200">
                        <DataTable
                          embedded
                          hideToolbar
                          tableId="super-admin-email-log-recipients"
                          columns={recipientColumns}
                          data={recipients}
                          getRowKey={row => `${row.role}-${row.email}`}
                          emptyMessage="No recipients listed for this email."
                          headerRowClassName="border-b border-zinc-200 bg-zinc-50/90 hover:bg-zinc-50/90"
                          rowClassName={() => "border-zinc-100"}
                        />
                      </div>
                    </div>
                  </DataTableProvider>

                  {/* Email body — Plain text / HTML tabs */}
                  <div className="md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <Mail className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Email content
                    </h3>
                    <Tabs
                      key={String(log.id)}
                      defaultValue="plain"
                      className="w-full"
                    >
                      <TabsList className="h-auto w-full justify-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50/80 p-1">
                        <TabsTrigger
                          value="plain"
                          className="w-full gap-2 rounded-md px-4 py-2 data-[state=active]:border data-[state=active]:border-[#f0dede] data-[state=active]:bg-white data-[state=active]:text-[#8B3232] data-[state=active]:shadow-sm"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Plain text body
                        </TabsTrigger>
                        <TabsTrigger
                          value="html"
                          disabled={htmlBody === "—"}
                          className="w-full gap-2 rounded-md px-4 py-2 disabled:opacity-40 data-[state=active]:border data-[state=active]:border-[#f0dede] data-[state=active]:bg-white data-[state=active]:text-[#8B3232] data-[state=active]:shadow-sm"
                        >
                          <Code className="h-4 w-4" />
                          HTML preview
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent
                        value="plain"
                        className="mt-4 focus-visible:ring-0"
                      >
                        <div className="border-y border-zinc-200 py-4 pl-4 pr-4">
                          <p className="whitespace-pre-wrap border-l-[3px] border-[#8B3232] pl-4 text-sm font-medium leading-relaxed text-zinc-600">
                            {plainBody}
                          </p>
                        </div>
                      </TabsContent>

                      <TabsContent
                        value="html"
                        className="mt-4 focus-visible:ring-0"
                      >
                        {htmlBody !== "—" ? (
                          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
                            <div
                              className="max-h-64 overflow-auto p-4 text-sm text-zinc-800 [scrollbar-color:#8B3232_#f4f4f5] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#8B3232] [&::-webkit-scrollbar-track]:bg-zinc-100 [&::-webkit-scrollbar]:w-2 [&_a]:text-blue-600 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_li]:ml-4 [&_p]:mb-2 [&_ul]:list-disc"
                              dangerouslySetInnerHTML={{ __html: htmlBody }}
                            />
                          </div>
                        ) : (
                          <p className="py-8 text-center text-sm text-muted-foreground">
                            No HTML content available for this email.
                          </p>
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-3 border-t border-zinc-200 bg-white px-6 py-4 sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-400 [&_svg]:text-zinc-400">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <p className="break-all text-sm font-semibold text-zinc-900">{value}</p>
      </div>
    </div>
  );
}
